import React, { useEffect, useMemo, useRef, useState } from "react";

import { FilterData, FilterDataItem } from "./store/model";
import { useAppDispatch, useAppSelector } from "application/store";
import { requestSelector } from "common/responseHandlerService/slice";
import { valuationSelector, setFilterMetric, reset } from "./store/slice";
import DropGroup from "application/components/DropGroup";
import {
  Title,
  Container,
  DropContainer,
  FiltersContainer,
  TitleWrapper,
  CompareButton,
  TableInfoWrapper,
  BottomLogo,
  UnderContentInfo,
  ArrowBackContainer,
  ArrowBack,
  BottomMfruLogo,
  LogoWrapper,
  SubTitle,
  OpenChartIcon,
} from "./styles";
import { Loader } from "../../features/chart/styles";
import SmallLoader from "../../application/components/SmallLoader";
import StickyHeaderTable from "../../application/components/StickyHeaderTable";
import MainContainer from "../../application/components/MainContainer";
import MSCIInfo from "../../application/components/MSCIInfo";
import {
  Data,
  NoData,
} from "../../application/components/StickyHeaderTable/types";
import { find, propEq } from "ramda";
import logoBlueSource from "../../application/assets/images/logo-blue.svg";
import { applicationConfig } from "../../application/config/applicationConfig";
import ReportLastUpdateAction from "../home/actions/ReportLastUpdateAction";
import LastUpdateDate from "../../application/components/LastUpdateDate";
import NotifyPopUpTable from "../../application/components/NotifyPopUpTable";
import { useMediaQuery, useTheme } from "@mui/material";
import mfruLogoSource from "../../application/assets/images/mfru-logo.svg";
import ValuationSummaryActions from "./store/actions";
import { formatDate } from "application/helpers/dateFormatter";
import OptionalTooltip from "application/components/OptionalTooltip";
import ChartModal from "application/components/ChartModal";
import { useLocation, useSearchParams } from "react-router-dom";

function getMetricByCode(code: string, data: FilterData) {
  for (const key in data) {
    const array = data[key];
    const foundObject = array.find((obj) => obj.code === code);
    if (foundObject) {
      return foundObject;
    }
  }
  return null;
}

const OpenChartAction = ({
  onClickHandler,
}: {
  onClickHandler: () => void;
}) => {
  return <OpenChartIcon onClick={onClickHandler} />;
};

const ValuationSummary = () => {
  const [selectedCount, setSelectedCount] = React.useState<number>(0);
  const [compare, setCompare] = React.useState<boolean>(false);
  const [isMSCIVisible, setIsMSCIVisible] = React.useState<boolean>(false);
  const [selectedCompare, setSelectedCompare] = React.useState<Data[]>([]);
  const [date, setDate] = useState<string>("");
  const buttonSectionRef = useRef<HTMLDivElement>(null);
  const [buttonSectionHeight, setButtonSectionHeight] = useState<number>(0);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down(1160));
  const isMobile = useMediaQuery(theme.breakpoints.down(1024));

  const dispatch = useAppDispatch();
  const requestData = useAppSelector(requestSelector);
  const { filters, tableData, metrics } = useAppSelector(valuationSelector);

  const [selectedIndex, setSelectedIndex] = useState<{
    code: string;
    name: string;
    description?: string;
  }>();

  const openChart = (currIndex: {
    code: string;
    name: string;
    description?: string;
  }) => setSelectedIndex(currIndex);
  const closeChart = () => setSelectedIndex(undefined);

  const tableRowsData = useMemo(() => {
    if (tableData?.stockSummaryData?.length) {
      return tableData?.stockSummaryData.map((d: any) => {
        const indexObj = {
          code: d.indexCode,
          name: d.indexName,
          description: d.description,
        };

        return {
          ...d,
          action: (
            <OpenChartAction
              onClickHandler={() => {
                openChart(indexObj);
              }}
            />
          ),
        };
      });
    }

    return tableData;
  }, [tableData]);

  const loading = requestData.getExpectedStockTableData.loading;

  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  useEffect(() => {
    Promise.all([dispatch(ValuationSummaryActions.getMetrics())]).then(
      (resArr) => {
        const queryParams = new URLSearchParams(location.search);
        const metricFromQueryParams = queryParams.get("metric");

        const metricRes = resArr[0].payload as FilterData;
        const metricKeys = Object.keys(metricRes);

        const selectedMetricFromRedirect =
          metricFromQueryParams !== null
            ? getMetricByCode(metricFromQueryParams, metricRes)
            : undefined;

        const initialMetric =
          selectedMetricFromRedirect ?? metricRes[metricKeys[0]][0];

        handleMetricSelect(initialMetric);

        if (metricFromQueryParams !== null) {
          setSearchParams("");
        }
      }
    );

    if (!date.length) {
      Promise.all([ReportLastUpdateAction.getLastUpdateDate()]).then((data) => {
        setDate(data[0]?.lastUpdatedDate || "");
      });
    }

    if (buttonSectionRef.current) {
      setButtonSectionHeight(buttonSectionRef.current.clientHeight);
    }

    return () => {
      dispatch(reset());
    };
  }, []);

  useEffect(() => {
    if (filters.selectedMetric.code) {
      dispatch(ValuationSummaryActions.getValuationSummaryTableData());
    }
  }, [filters]);

  useEffect(() => {
    let arr: Data[] = [];
    selectedCompare.map((item: Data) => {
      // @ts-ignore
      let dataItem: Data | NoData = find(propEq("indexName", item.indexName))(
        tableData.stockSummaryData
      );

      if (!dataItem) {
        dataItem = {
          ...item,
          noData: true,
        };
      } else {
        dataItem = {
          ...dataItem,
          action: item.action,
        };
      }

      return (arr = [...arr, dataItem]);
    });

    setSelectedCompare(arr);
  }, [tableData]);

  const headCells: any[] = [
    {
      id: "indexName",
      label: "Index",
      diffBackground: false,
    },
    {
      id: "asOfThreeMonthAgo",
      label: `As of ${
        tableData?.stockSummaryData?.length
          ? formatDate(tableData.stockSummaryData[0]?.asOfThreeMonthAgoDate)
          : ""
      }`,
      diffBackground: false,
    },
    {
      id: "asOfTwoMonthAgo",
      label: `As of ${
        tableData?.stockSummaryData?.length
          ? formatDate(tableData.stockSummaryData[0]?.asOfTwoMonthAgoDate)
          : ""
      }`,
      diffBackground: false,
    },
    {
      id: "asOfOneMonthAgo",
      label: `As of ${
        tableData?.stockSummaryData?.length
          ? formatDate(tableData?.stockSummaryData[0]?.asOfOneMonthAgoDate)
          : ""
      }`,
      diffBackground: false,
    },
    {
      id: "longTermAverage",
      label: "Long Term Average",
      diffBackground: false,
      tooltip: "The average for the metric since its starting year.",
    },
    {
      id: "standardDeviation",
      label: "Standard Deviations",
      diffBackground: false,
      tooltip:
        "A statistical measure of how far the most recent data point is from the long-term average, expressed in standard deviations. Positive for above-average, negative for below. Larger deviations (up or down) indicate rarer extremes.",
    },
    {
      id: "longTermAverageStartingYear",
      label: "Long Term Average Starting Year",
      diffBackground: false,
      tooltip: "The first year the metric became available.",
    },
    {
      id: "action",
      label: "View Chart",
      diffBackground: false,
    },
  ];

  const handleMetricSelect = (item: FilterDataItem) => {
    dispatch(setFilterMetric(item));
  };

  const showButton = (show: number) => {
    setSelectedCount(show);
  };

  const compareLines = () => {
    setCompare(!compare);
  };

  const toggleMSCIVisible = () => {
    setIsMSCIVisible(!isMSCIVisible);
  };

  const handleSelectedCompare = (data: Data[]) => {
    setSelectedCompare(data);
  };

  return (
    <div>
      <MainContainer>
        <Container>
          <TitleWrapper>
            <Title>Stock Valuations and Earnings Summary Tables</Title>
            <LastUpdateDate date={date} />
          </TitleWrapper>
          <FiltersContainer>
            <DropContainer>
              <DropGroup
                data={metrics}
                label="Specific metric"
                disabled={loading}
                onSelect={handleMetricSelect}
                selected={filters.selectedMetric}
                placeholder="Specific metric name goes here"
              />
            </DropContainer>
          </FiltersContainer>
          {loading && (
            <Loader>
              <SmallLoader />
            </Loader>
          )}
          {!loading && (
            <div>
              <TableInfoWrapper ref={buttonSectionRef} compare={compare}>
                <OptionalTooltip text={filters.selectedMetric.description}>
                  <SubTitle>{filters.selectedMetric.name}</SubTitle>
                </OptionalTooltip>

                {!compare && (
                  <CompareButton
                    onClick={compareLines}
                    disabled={selectedCount < 2}
                  >
                    COMPARE {isMobile ? "" : " INDEXES"}
                  </CompareButton>
                )}
                {compare && (
                  <>
                    <CompareButton compare={compare} onClick={compareLines}>
                      back to full list
                    </CompareButton>
                    <ArrowBackContainer onClick={compareLines}>
                      <ArrowBack />
                    </ArrowBackContainer>
                  </>
                )}
              </TableInfoWrapper>
              {tableData?.stockSummaryData?.length && (
                <StickyHeaderTable
                  historicalStock={tableRowsData}
                  dataUnitsOfMeasurement={tableData.metricUnitsOfMeasurement}
                  showButton={showButton}
                  compareLines={compare}
                  headCells={headCells}
                  onSelectedCompare={handleSelectedCompare}
                  selectedCompare={selectedCompare}
                  buttonSection={buttonSectionHeight}
                  valuationSummary={true}
                />
              )}
              <UnderContentInfo>
                <MSCIInfo
                  isMSCIVisible={isMSCIVisible}
                  underTable
                  onChange={toggleMSCIVisible}
                />
                <LogoWrapper>
                  <BottomMfruLogo
                    src={mfruLogoSource}
                    alt="logo"
                    onClick={() => {
                      window.open(applicationConfig.main_page, "_blank");
                    }}
                  />
                  <BottomLogo
                    src={logoBlueSource}
                    alt="logo"
                    onClick={() => {
                      window.open(applicationConfig.assetcamp, "_blank");
                    }}
                  />
                </LogoWrapper>
              </UnderContentInfo>
            </div>
          )}
        </Container>
        {isTablet && <NotifyPopUpTable />}
      </MainContainer>
      {selectedIndex && (
        <ChartModal
          onClose={closeChart}
          selectedIndex={selectedIndex}
          selectedMetric={filters.selectedMetric}
        />
      )}
    </div>
  );
};

export default ValuationSummary;
